// Placa realista (pedido 2026-10-19: "aonde aparece as placas dos veículos
// em todos os visual aparece uma placa realista bem legal"). Mercosul
// (ABC1D23): branca com a faixa azul "BRASIL" + bandeira e "BR" na
// lateral. Antiga (ABC1234): cinza, "ABC-1234". O que não é placa (ex.:
// veículo sem placa, onde o sistema mostra o IMEI no lugar) sai como texto
// simples — nunca finge ser placa. Tudo em `em`: o tamanho vem só do
// font-size de cada `size`, então escala junto em qualquer tela.
import { cn } from "@/lib/utils";

type PlateSize = "xs" | "sm" | "md" | "lg";

const SIZE_FONT: Record<PlateSize, string> = {
  xs: "text-[9px]",
  sm: "text-[11px]",
  md: "text-[14px]",
  lg: "text-[20px]",
};

const MERCOSUL = /^[A-Z]{3}\d[A-Z]\d{2}$/;
const OLD = /^[A-Z]{3}\d{4}$/;

export function parsePlate(raw: string | null | undefined): { kind: "mercosul" | "old"; text: string } | null {
  const s = (raw ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (MERCOSUL.test(s)) return { kind: "mercosul", text: s };
  if (OLD.test(s)) return { kind: "old", text: `${s.slice(0, 3)}-${s.slice(3)}` };
  return null;
}

const PLATE_FONT = { fontFamily: '"Roboto Condensed", "Arial Narrow", "Helvetica Neue", Arial, sans-serif' };

function BrazilFlag() {
  return (
    <svg viewBox="0 0 20 14" className="h-[0.75em] w-auto shrink-0" aria-hidden>
      <rect width="20" height="14" fill="#009c3b" />
      <path d="M10 1.6 18.2 7 10 12.4 1.8 7z" fill="#ffdf00" />
      <circle cx="10" cy="7" r="3.1" fill="#002776" />
    </svg>
  );
}

export function LicensePlate({
  plate,
  size = "sm",
  className,
}: {
  plate: string | null | undefined;
  size?: PlateSize;
  className?: string;
}) {
  const parsed = parsePlate(plate);
  if (!parsed) {
    if (!plate) return null;
    return <span className={cn("font-mono font-semibold", SIZE_FONT[size], className)}>{plate}</span>;
  }

  if (parsed.kind === "old") {
    return (
      <span
        title={`Placa ${parsed.text}`}
        className={cn(
          "inline-flex shrink-0 select-none items-center whitespace-nowrap rounded-[0.3em] border-[0.12em] border-slate-500 bg-gradient-to-b from-slate-200 to-slate-300 px-[0.45em] py-[0.12em] align-middle font-extrabold leading-none tracking-[0.06em] text-slate-900 shadow-[inset_0_0_0_0.08em_rgba(255,255,255,0.7),0_1px_1.5px_rgba(0,0,0,0.25)]",
          SIZE_FONT[size],
          className,
        )}
        style={PLATE_FONT}
      >
        {parsed.text}
      </span>
    );
  }

  return (
    <span
      title={`Placa ${parsed.text}`}
      className={cn(
        "inline-flex shrink-0 select-none flex-col overflow-hidden whitespace-nowrap rounded-[0.3em] border-[0.12em] border-slate-800 bg-white align-middle leading-none text-slate-950 shadow-[0_1px_1.5px_rgba(0,0,0,0.3)]",
        SIZE_FONT[size],
        className,
      )}
      style={PLATE_FONT}
    >
      <span className="flex items-center justify-between gap-[0.3em] bg-gradient-to-b from-[#1f4fa8] to-[#0b3a8f] px-[0.3em] py-[0.1em] text-[0.42em] font-bold leading-[1.2] tracking-[0.12em] text-white">
        <span className="opacity-0">··</span>
        <span>BRASIL</span>
        <BrazilFlag />
      </span>
      <span className="flex items-center gap-[0.25em] px-[0.35em] py-[0.1em]">
        <span className="text-[0.38em] font-bold leading-none text-slate-700">BR</span>
        <span className="font-extrabold tracking-[0.08em]">{parsed.text}</span>
      </span>
    </span>
  );
}
